import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  ICategoriaUbicacion,
  ICliente,
  IFilter,
  IGeoJSONPolygon,
  ILayerMapaPersonalizado,
  IPopulate,
  IQueryParam,
  IRecorrido,
  IReporteBase,
  IReporteGenerico,
  ITrackeo,
  IUbicacion,
} from 'modelos/src';
import { firstValueFrom, reduce, Subscription } from 'rxjs';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { TipoMapaBase } from '../../../standalone/selector-tipo-mapa/selector-tipo-mapa.component';
import { LoginService } from '../../../login/login.service';
import { MapaFacilV2Service } from '../../../../auxiliares/mapbox/mapa-facil-v2.service';
import mapboxgl from 'mapbox-gl';
@Component({
  selector: 'app-mapa-colectivos',
  templateUrl: './mapa-colectivos.component.html',
  styleUrl: './mapa-colectivos.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MapaColectivosComponent implements OnInit, OnDestroy {
  private lista2 = inject(Lista2Service);
  private helper = inject(HelperService);
  private mapaFacilV2Service = inject(MapaFacilV2Service);
  private cdr = inject(ChangeDetectorRef);

  public ctrl = false;
  //Vehiculos
  public reportes?: IReporteBase<'Tracker 4G'>[] = [];
  public reportes$?: Subscription;
  public reporteSeleccionado?: IReporteBase<'Tracker 4G'>;

  // Recorridos
  public recorridos: IRecorrido[] = [];
  public recorridos$?: Subscription;
  public recorridosSeleccionados: IRecorrido[] = [];

  // Entidades permitidas para re-aplicar datos al cambiar capa base. Las entidades pueden ser las que están presentes en MapaFacilV2Data
  public allowedTypes: string[] = ['colectivos'];

  public popup$?: Subscription;
  // Trackeo
  public trackeo?: ITrackeo;

  // Mapa
  public map: mapboxgl.Map | null = null;

  // Paradas del colectivo seleccionado
  private paradasMostradas: any[] = [];

  //Nombre del mapa
  public nombreMap = 'MapaColectivosComponent';

  //MAPAS A MOSTRAR
  public readonly TIPO_MAPA_BASE_KEY = 'tipoMapaBaseColectivosComponent'; //Key para ver el mapa base que está en el local Storage
  public tipoMapaBase: TipoMapaBase =
    localStorage.getItem(this.TIPO_MAPA_BASE_KEY) || 'osm'; //Mapa base que está en el local Storage
  public mapasPersonalizados: ILayerMapaPersonalizado[] = []; //Mapas personalizados del cliente
  public baseLayer: any; //Capa base que va a poner en el mapa
  public clientePropio: ICliente = LoginService.getClientePropio();

  // Popup
  public coordinatesPopup!: [number, number];
  public showPopup = false;

  //Propiedades para zoom
  public currentZoom = 16;

  /// Vehiculos
  private async listarReportes(): Promise<void> {
    const haceUnMes = new Date(new Date().setMonth(new Date().getMonth() - 1));
    const filter: IFilter<IReporteGenerico> = {
      tipoEntidad: 'Colectivo',
      fechaCreacion: { $gte: haceUnMes.toISOString() },
    };
    const populate: IPopulate = [
      {
        path: 'activo',
        select: 'identificacion categoria vehiculo',
        populate: {
          path: 'vehiculo.recorrido vehiculo.recorridos',
          populate: {
            path: 'paradas', // Populate paradas del recorrido
          },
        },
      },
      {
        path: 'grupos',
        select: 'nombre color',
      },
      {
        path: 'tracker',
        select: 'nombre tipo identificacion',
      },
      {
        path: 'cliente',
        select: 'nombre tipoCliente',
      },
      {
        path: 'recorrido',
        populate: {
          path: 'paradas', // También populate paradas del recorrido directo
        },
      },
    ];
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      populate: JSON.stringify(populate),
      includeChildren: true,
    };

    this.reportes$?.unsubscribe();
    this.reportes$ = this.lista2
      .ultimosReportesGenericos<'Tracker 4G'>(query, 15)
      .subscribe((data) => {
        if (!this.recorridos.length) {
          this.helper.notifError('No se encontraron recorridos');
        }

        this.reportes = data.datos;
        this.addMarkers();

        if (this.reporteSeleccionado) {
          this.reporteSeleccionado = this.reportes.find(
            (r) =>
              r.idsAsignados[0] === this.reporteSeleccionado.idsAsignados[0],
          );
        }
        // OnPush: Marcar para change detection después del polling
        this.cdr.markForCheck();
      });
  }

  private async listarRecorridos(): Promise<void> {
    const filter: IFilter<IRecorrido> = { categoria: 'Colectivo' };
    const populate: IPopulate[] = [
      {
        path: 'grupo',
        select: 'nombre color',
      },
      {
        path: 'ubicaciones',
      },
    ];
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      populate: JSON.stringify(populate),
      sort: 'nombre',
      includeChildren: true,
      childrenLevel: 0,
    };
    this.recorridos$?.unsubscribe();
    this.recorridos$ = this.lista2.recorridos(query, 30).subscribe((data) => {
      this.recorridos = data.datos;
      this.addPolyLines();
      // OnPush: Marcar para change detection después del polling
      this.cdr.markForCheck();
    });
  }

  private async listarUltimoTrackeo(idActivo: string) {
    try {
      this.trackeo = await firstValueFrom(
        this.lista2.ultimoTrackeoPorIdActivo(idActivo),
      );
    } catch (error: any) {
      this.helper.notifError(
        `Error al listar el último trackeo del activo ${idActivo}`,
      );
    }
  }

  //

  private addMarkers() {
    if (!this.mapaFacilV2Service.isReady()) return;

    let reportes = this.reportes || [];

    // Filtrar por recorridos seleccionados si hay alguno
    if (this.recorridosSeleccionados.length > 0) {
      const idsRecorridos = this.recorridosSeleccionados.map((r) => r._id);
      reportes = reportes.filter((reporte) =>
        idsRecorridos.includes(reporte.activo?.vehiculo?.idRecorrido),
      );
    }

    // API simplificada: pasamos los reportes directamente
    this.mapaFacilV2Service.updateData({
      colectivos: reportes,
    });
  }

  /// Ver Terminales de los recorridos elegidos
  public mostrarTerminales(terminales: IUbicacion[], color: string) {
    // TODO: Migrar a nueva API con PolygonsLayerManager
    // let ubicaciones: any[] = [];
    // terminales.map((t) => {
    //   const coords = (t.geojson?.coordinates as [[number, number][]]) || [];
    //   ubicaciones.push({
    //     points: coords,
    //     color,
    //     width: 4,
    //     fillColor: 'grey',
    //   });
    // });
    // this.mapaFacilV2Service.updateData({ polygons: ubicaciones });
  }

  private addPolyLines() {
    if (!this.mapaFacilV2Service.isReady()) return;

    // Determinar IDs de recorridos seleccionados
    const idsSeleccionados = this.recorridosSeleccionados.map((r) => r._id);
    const haySeleccion = idsSeleccionados.length > 0;

    // Detectar si el mapa está en modo oscuro
    const isDarkMode = this.mapaFacilV2Service.isMapaDarkMode(
      this.tipoMapaBase,
    );

    // Convertir TODOS los recorridos a formato de polylines
    // Ajustando la opacidad según si están seleccionados o no y el modo del mapa
    const polylines = this.recorridos
      .filter((r) => r.geojson?.coordinates) // Solo recorridos con coordenadas válidas
      .map((r) => {
        // Prioridad: color del recorrido PRIMERO, luego grupo, luego fallback
        const color = r.color || r.grupo?.color || '#ff7300';

        // Determinar opacidad según selección y modo oscuro
        let opacity: number;
        if (!haySeleccion) {
          // Sin filtro: más opaco en modo oscuro para mejor visibilidad
          opacity = isDarkMode ? 0.95 : 0.7;
        } else {
          // Con filtro: ajustar opacidades para modo oscuro
          if (idsSeleccionados.includes(r._id)) {
            // Seleccionados: totalmente opacos en modo oscuro
            opacity = isDarkMode ? 1.0 : 0.9;
          } else {
            // No seleccionados: un poco más visibles en modo oscuro
            opacity = isDarkMode ? 0.25 : 0.15;
          }
        }

        return {
          id: r._id,
          points: r.geojson.coordinates as [number, number][],
          color: color,
          width: 4,
          opacity: opacity,
        };
      });

    this.mapaFacilV2Service.updateData({ polylines });
  }

  private armarRecorridoMapa(r: IRecorrido, opacity: number) {
    // TODO: Migrar a nueva API
    return null as any;
  }

  public async onRecorridosChange(r: IRecorrido[]) {
    this.recorridosSeleccionados = r;
    this.addPolyLines();
    this.addMarkers();
    this.reporteSeleccionado = undefined;
    this.trackeo = undefined;
  }

  /**
   * Maneja el evento de click en entidades del mapa
   * Cuando se hace click en un colectivo, carga las paradas de su recorrido
   */
  public async onPopupData(data: any) {
    if (data?.tipo === 'colectivos') {
      const colectivo = data.data;

      // Cargar último trackeo del colectivo
      if (colectivo?.activo?._id) {
        await this.listarUltimoTrackeo(colectivo.activo._id);
      }

      this.reporteSeleccionado = colectivo;

      // Cargar paradas del recorrido
      const recorrido = colectivo?.activo?.vehiculo?.recorrido;
      if (recorrido) {
        await this.addParadas(recorrido);
      } else {
        console.warn('⚠️ Colectivo sin recorrido:', colectivo);
        // Limpiar paradas anteriores
        this.mapaFacilV2Service.updateData({ paradas: [] });
      }

      // OnPush: Marcar para change detection
      this.cdr.markForCheck();
    } else {
      // Si se hace click en otra entidad (no colectivo), limpiar paradas
      this.mapaFacilV2Service.updateData({ paradas: [] });
      this.reporteSeleccionado = undefined;
      this.trackeo = undefined;
      this.cdr.markForCheck();
    }
  }

  /**
   * Agrega las paradas de un recorrido al mapa
   */
  private async addParadas(recorrido?: IRecorrido) {
    if (!recorrido) {
      this.mapaFacilV2Service.updateData({ paradas: [] });
      return;
    }

    const paradas = recorrido.paradas;
    if (!paradas || paradas.length === 0) {
      this.mapaFacilV2Service.updateData({ paradas: [] });
      return;
    }

    // Actualizar paradas en el mapa usando la nueva API
    this.mapaFacilV2Service.updateData({ paradas });
    this.paradasMostradas = paradas;
  }

  private async addCircle() {
    // TODO: Migrar círculos a nueva API
  }

  public initMapa() {
    // Suscribirse a eventos de popup para detectar clicks en colectivos
    this.popup$?.unsubscribe();
    this.popup$ = this.mapaFacilV2Service.popupDatos$.subscribe((data) => {
      if (data) {
        // Popup abierto con datos
        this.onPopupData(data);
      } else {
        // Popup cerrado (data === null)
        this.mapaFacilV2Service.updateData({ paradas: [] });
        this.reporteSeleccionado = undefined;
        this.trackeo = undefined;
        this.cdr.markForCheck();
      }
    });
  }

  ///
  public onMapReady(map: mapboxgl.Map): void {
    this.map = map;

    // Inicializar handlers de popup
    this.initMapa();

    // Volver a cargar datos que ya están en memoria
    this.addMarkers();
    this.addPolyLines();
    // OnPush: Marcar para check por si el mapa se carga después del polling
    this.cdr.markForCheck();
  }

  async ngOnInit() {
    if (this.clientePropio?.mapLayers) {
      this.mapasPersonalizados = this.clientePropio.mapLayers;
    }
    await Promise.all([await this.listarRecorridos()]);
    await Promise.all([await this.listarReportes()]);
  }

  ngOnDestroy() {
    this.reportes$?.unsubscribe();
    this.recorridos$?.unsubscribe();
    this.popup$?.unsubscribe();

    // IMPORTANTE: Limpiar datos del servicio para evitar que persistan al cambiar de mapa
    // El servicio es singleton, por lo que mantiene datos entre componentes
    this.mapaFacilV2Service.clearAllData();
  }
}
